import { FormEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  IdentityA11yAnnouncer,
  IdentityButton,
  IdentityFooterInline,
  IdentityHero,
  IdentityLayout,
} from "@speedycore/identity-ui";
import {
  acceptLegalTerms,
  getCurrentLegalVersion,
  getLegalAcceptance,
  redirectAfterAuthWithHandoff,
  sendIdentityTelemetry,
} from "../lib/identityApi";
import { AiAcknowledgement } from "../components/AiAcknowledgement";

const LEGAL_BULLETS = [
  "Terms or Privacy have been updated since your last acceptance",
  "Review both documents below before continuing",
  "One acceptance covers the entire SpeedyCore platform",
] as const;

const FALLBACK_TERMS_VERSION = "2026-10-03";
const FALLBACK_PRIVACY_VERSION = "2026-08-14";

export function LegalUpdatePage() {
  const [params] = useSearchParams();
  const redirectUri = useMemo(() => params.get("redirect_uri"), [params]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [a11y, setA11y] = useState<string>("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [aiAccepted, setAiAccepted] = useState(false);
  const [termsVersion, setTermsVersion] = useState<string>(FALLBACK_TERMS_VERSION);
  const [privacyVersion, setPrivacyVersion] = useState<string>(FALLBACK_PRIVACY_VERSION);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [versions, status] = await Promise.all([
          getCurrentLegalVersion(),
          getLegalAcceptance().catch(() => null),
        ]);
        if (cancelled) return;
        if (versions?.terms_version) setTermsVersion(versions.terms_version);
        if (versions?.privacy_version) setPrivacyVersion(versions.privacy_version);
        if (
          status &&
          status.accepted &&
          !status.needs_reaccept &&
          status.terms_version === (versions?.terms_version || FALLBACK_TERMS_VERSION) &&
          status.privacy_version === (versions?.privacy_version || FALLBACK_PRIVACY_VERSION)
        ) {
          if (redirectUri) void redirectAfterAuthWithHandoff(redirectUri);
          else window.location.assign("https://dashboard.speedycore.com.au/app/");
        }
      } catch {
        /* fall through */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [redirectUri]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!legalAccepted) {
      const m = "You must accept the updated Terms & Conditions and Privacy Policy to continue.";
      setError(m);
      setA11y(m);
      return;
    }
    if (!aiAccepted) {
      const m = "Please acknowledge the AI accuracy responsibility notice to continue.";
      setError(m);
      setA11y(m);
      return;
    }
    setBusy(true);
    try {
      await acceptLegalTerms({ terms_version: termsVersion, privacy_version: privacyVersion, ai_acknowledged: true });
      if (redirectUri) void redirectAfterAuthWithHandoff(redirectUri);
      else window.location.assign("https://dashboard.speedycore.com.au/app/");
    } catch (err) {
      const code = (err as Error & { code?: string }).code;
      const msg = err instanceof Error ? err.message : "Could not accept legal terms.";
      void sendIdentityTelemetry("identity.legal_update_failed", {
        error_code: code || "legal_acceptance_failed",
      });
      setError(code === "VERSION_REQUIRED"
        ? "A newer version of the legal terms became available while you were reviewing. Please refresh and accept the latest version."
        : msg);
      setA11y(msg);
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <IdentityLayout
        hero={
          <IdentityHero
            title="Legal Update Required"
            subtitle="Checking for the latest Speedy Systems Pty Ltd legal versions…"
            bullets={LEGAL_BULLETS}
          />
        }
        form={<div style={{ padding: "1rem" }}>Loading legal documents…</div>}
        footer={<IdentityFooterInline />}
      />
    );
  }

  return (
    <>
      <IdentityA11yAnnouncer message={a11y} type="error" />
      <IdentityLayout
        hero={
          <IdentityHero
            title="Legal Update Required"
            subtitle="Speedy Systems Pty Ltd has updated the master Terms & Conditions or Privacy Policy. Please review and re-accept to continue using all SpeedyCore apps."
            bullets={LEGAL_BULLETS}
          />
        }
        form={
          <form onSubmit={onSubmit} noValidate>
            <div style={{ display: "grid", gap: "1rem", marginBottom: "1.25rem" }}>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: "0.75rem", padding: "1rem 1.25rem", backgroundColor: "#f9fafb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#111827", fontSize: "0.95rem" }}>Master Terms &amp; Conditions</div>
                    <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.15rem" }}>{termsVersion}</div>
                  </div>
                  <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.85rem", color: "#1e3a8a", textDecoration: "underline", whiteSpace: "nowrap" }}>
                    View →
                  </Link>
                </div>
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: "0.75rem", padding: "1rem 1.25rem", backgroundColor: "#f9fafb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#111827", fontSize: "0.95rem" }}>Master Privacy Policy</div>
                    <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.15rem" }}>{privacyVersion}</div>
                  </div>
                  <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.85rem", color: "#1e3a8a", textDecoration: "underline", whiteSpace: "nowrap" }}>
                    View →
                  </Link>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "0.75rem", marginBottom: "1.25rem" }}>
              <label style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", fontSize: "0.85rem", color: "#1f2937", cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  id="speedycore-legal-consent"
                  name="speedycore_legal_consent"
                  checked={legalAccepted}
                  onChange={(e) => setLegalAccepted(e.target.checked)}
                  style={{ marginTop: "0.2rem", transform: "scale(1.05)" }}
                  aria-describedby="speedycore-legal-text"
                  required
                />
                <span id="speedycore-legal-text" style={{ lineHeight: 1.5 }}>
                  I confirm I am at least <strong>18 years old</strong>, or the age of majority in my jurisdiction,
                  and I agree to the updated Speedy Systems Pty Ltd{" "}
                  <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#1e3a8a", textDecoration: "underline" }}>Terms &amp; Conditions</Link>{" "}and{" "}
                  <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1e3a8a", textDecoration: "underline" }}>Privacy Policy</Link>.
                </span>
              </label>
            </div>

            <AiAcknowledgement checked={aiAccepted} onChange={setAiAccepted} />

            {error ? (
              <div style={{ marginBottom: "1rem", padding: "0.75rem 1rem", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "0.5rem", color: "#991b1b", fontSize: "0.85rem" }} role="alert">
                {error}
              </div>
            ) : null}

            <IdentityButton type="submit" loading={busy} variant="primary" fullWidth>
              Accept Update &amp; Continue
            </IdentityButton>

            <IdentityFooterInline />
          </form>
        }
      />
    </>
  );
}

export default LegalUpdatePage;
